import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { featureFlags } from "src/lib/featureFlags";
import {
  formatPhoneNumberNational,
  validatePhoneNumber
} from "src/lib/formatPhoneNumber";
import translate from "src/lib/translate";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import ProfileCubit from "src/state/ProfileCubit/ProfileCubit";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import AppPageTitle from "src/ui/components/AppPageTitle/AppPageTitle";
import BiometricAuthenticationSetup from "src/ui/components/BiometricAuthenticationSetup/BiometricAuthenticationSetup";
import ChangeEmail from "src/ui/components/ChangeEmail/ChangeEmail";
import ChangeNotificationNumber from "src/ui/components/ChangeNotificationNumber/ChangeNotificationNumber";
import ChangePassword from "src/ui/components/ChangePassword/ChangePassword";
import ChangePreferredName from "src/ui/components/ChangePreferredName/ChangePreferredName";
import ChangePronouns from "src/ui/components/ChangePronouns/ChangePronouns";
import Data from "src/ui/components/Data/Data";
import { PROFILE_URL } from "src/ui/components/ProfileOverview/ProfileOverview";
import OverlayBackButton from "src/ui/components/StyledComponents/OverlayBackButton";
import TwoFactorAuthenticationSetup from "src/ui/components/TwoFactorAuthenticationSetup/TwoFactorAuthenticationSetup";

const accountPath = "/app/profile/account";

enum AccountInformationAction {
  changePreferredName = "changePreferredName",
  changePronouns = "changePronouns",
  changeEmail = "changeEmail",
  changePassword = "changePassword",
  changePhoneNumber = "changePhoneNumber"
}

const AccountInformation: FC = () => {
  const [profile, { loadData, clearAllData }] = useBlocNext(ProfileCubit);
  const [, { nickname, pronouns, loadUserPreferences }] =
    useBlocNext(UserPreferencesCubit);
  const navigate = useNavigate();
  const { action = "" } = useParams<{ action?: AccountInformationAction }>();
  const [{ partnerSession }] = useBlocNext(AppViewCubit);

  const title = useMemo(() => {
    switch (action) {
      case AccountInformationAction.changePreferredName:
        return translate("account.title.editPreferredName");
      case AccountInformationAction.changePronouns:
        return translate("account.title.editPronouns");
      case AccountInformationAction.changeEmail:
        return translate("account.title.editEmailAddress");
      case AccountInformationAction.changePassword:
        return translate("account.title.editPassword");
      case AccountInformationAction.changePhoneNumber:
        return translate("account.title.editPhoneNumber");
      default:
        return translate("account.title.default");
    }
  }, [action]);

  const backUrl = useMemo(() => {
    switch (action) {
      case AccountInformationAction.changePreferredName:
      case AccountInformationAction.changePronouns:
      case AccountInformationAction.changeEmail:
      case AccountInformationAction.changePassword:
      case AccountInformationAction.changePhoneNumber:
        return accountPath;
      default:
        return PROFILE_URL;
    }
  }, [action]);

  useEffect(() => {
    clearAllData();
    void loadData();
    void loadUserPreferences();
  }, []);

  const goto = (goToAction: AccountInformationAction) => () => {
    navigate(`${accountPath}/${goToAction}`);
  };

  const phoneNumber = profile.data.number?.number;
  const pnoneNumberValid = phoneNumber
    ? validatePhoneNumber(phoneNumber)
    : true;
  const formattedPhoneNumber = formatPhoneNumberNational(phoneNumber, false, {
    noValidation: true
  });

  return (
    <>
      <OverlayBackButton show fallbackUrl={backUrl} />
      <AppPageTitle title={title}></AppPageTitle>
      <nine-spacer s={"xl"}></nine-spacer>
      {action === "" && (
        <>
          <Data.Table
            title={translate("preferredName")}
            action={{
              onAction: goto(AccountInformationAction.changePreferredName)
            }}
          >
            <Data.Row
              data={nickname?.length ? nickname : "–"}
              ellipsis
              className={nickname?.length ? "" : "color-gray"}
            />
          </Data.Table>

          <Data.Table
            title={translate("pronouns")}
            action={{
              onAction: goto(AccountInformationAction.changePronouns)
            }}
          >
            <Data.Row
              data={pronouns?.length ? pronouns : "–"}
              ellipsis
              className={pronouns?.length ? "" : "color-gray"}
            />
          </Data.Table>

          <Data.Table
            title={translate("email")}
            action={{
              onAction: goto(AccountInformationAction.changeEmail),
              hidden: partnerSession
            }}
          >
            <Data.Row data={profile.data.email} ellipsis />
          </Data.Table>

          {!partnerSession && (
            <Data.Table
              title={translate("password")}
              action={{
                onAction: goto(AccountInformationAction.changePassword)
              }}
            >
              <Data.Row data={"************"} />
            </Data.Table>
          )}

          <Data.Table
            title={
              <>
                {translate("phoneNumber")}
                {!pnoneNumberValid && (
                  <nine-pill
                    style={{
                      marginLeft: "var(--space-xs)",
                      "--background-color": "var(--color-sunrise-orange)"
                    }}
                  >
                    {translate("info.number_invalid")}
                  </nine-pill>
                )}
              </>
            }
            action={{
              onAction: goto(AccountInformationAction.changePhoneNumber),
              hidden: partnerSession
            }}
          >
            <Data.Row data={formattedPhoneNumber} />
          </Data.Table>

          {featureFlags.flagMultiFactorAuth && !partnerSession && (
            <>
              <BiometricAuthenticationSetup />

              <Data.Section
                title={translate("account.title.twoFactorAuthentication")}
              >
                <TwoFactorAuthenticationSetup />
              </Data.Section>
            </>
          )}
        </>
      )}

      {action === AccountInformationAction.changePreferredName && (
        <ChangePreferredName />
      )}
      {action === AccountInformationAction.changePronouns && <ChangePronouns />}
      {action === AccountInformationAction.changeEmail && <ChangeEmail />}
      {action === AccountInformationAction.changePassword && <ChangePassword />}
      {action === AccountInformationAction.changePhoneNumber && (
        <ChangeNotificationNumber />
      )}
    </>
  );
};

export default AccountInformation;
